/**
 * Phase 2G.4 - branch operational & business comparison (READ-ONLY).
 *
 * One vocabulary for `GET /api/branch-comparison`: ten metric groups that
 * span transactions, workload/completion, pick-up, delivery, value,
 * discrepancies, exceptions, inventory, purchases and expenses. Every
 * number is aggregated from records the API already enforces - nothing is
 * written, scored, ranked or judged; interpretation stays with the Admin.
 *
 * Two explicit metric bases, so no number can be misread:
 *
 *   window    - counted from rows whose SERVER-stamped createdAt falls in
 *               the selected Asia/Manila business-date range (uniform
 *               rule, identical to Phase 2G.3; no range = all history).
 *   snapshot  - current state at `generatedAt` (queues, stock, exception
 *               detection). The date filter deliberately does NOT apply.
 *
 * Money stays in integer centavos; differences are raw sums (a negative
 * net difference simply means more items were short than over).
 */

/** Stable group ids - also the key each group's metrics live under. */
export const COMPARISON_GROUPS = [
  'transactions',
  'workload',
  'pickup',
  'delivery',
  'value',
  'discrepancies',
  'exceptions',
  'inventory',
  'purchases',
  'expenses',
] as const;
export type ComparisonGroupId = (typeof COMPARISON_GROUPS)[number];

export const COMPARISON_GROUP_LABELS: Record<ComparisonGroupId, string> = {
  transactions: 'Transactions',
  workload: 'Workload & completion',
  pickup: 'Pick-up',
  delivery: 'Delivery',
  value: 'Value',
  discrepancies: 'Item discrepancies',
  exceptions: 'Needs attention',
  inventory: 'Inventory',
  purchases: 'Purchases',
  expenses: 'Expenses',
};

/** One-line explanation shown above each group in the UI. */
export const COMPARISON_GROUP_HINTS: Record<ComparisonGroupId, string> = {
  transactions: 'Records received, work closed and the current backlog',
  workload: 'Processing steps finished in the period, plus open steps right now',
  pickup: 'Ready orders waiting on the counter and releases completed in the period',
  delivery: 'Delivery actions in the period and orders still moving today',
  value: 'Order value received in the period and how much of it is paid',
  discrepancies: 'Completed steps that counted a different number of items than expected',
  exceptions: 'Objective observations from the Needs Attention scan, per branch',
  inventory: 'Active stock items, low-stock rows and movements in the period',
  purchases: 'Purchase documents created in the period (posted spend highlighted)',
  expenses: 'Operating expenses recorded in the period',
};

export type ComparisonMetricUnit = 'count' | 'pesos' | 'delta';
/** `window` honours the date filter; `snapshot` is current state (see file header). */
export type ComparisonMetricBasis = 'window' | 'snapshot';

export interface ComparisonMetricDef {
  group: ComparisonGroupId;
  key: string;
  label: string;
  unit: ComparisonMetricUnit;
  basis: ComparisonMetricBasis;
}

/**
 * Every metric the API returns, in display order. The UI renders straight
 * from this list, and the shared test asserts it stays in exact sync with
 * `emptyComparisonMetrics()` - a new metric cannot ship without metadata
 * and stale metadata cannot survive.
 */
export const COMPARISON_METRICS: readonly ComparisonMetricDef[] = [
  // transactions
  { group: 'transactions', key: 'received', label: 'Received', unit: 'count', basis: 'window' },
  { group: 'transactions', key: 'done', label: 'Completed (closed)', unit: 'count', basis: 'window' },
  { group: 'transactions', key: 'activeNow', label: 'Active now', unit: 'count', basis: 'snapshot' },
  // workload
  { group: 'workload', key: 'processingCompleted', label: 'Steps completed', unit: 'count', basis: 'window' },
  { group: 'workload', key: 'foldingCompleted', label: 'Folding steps', unit: 'count', basis: 'window' },
  { group: 'workload', key: 'openSteps', label: 'Open steps now', unit: 'count', basis: 'snapshot' },
  // pickup
  { group: 'pickup', key: 'readyAwaiting', label: 'Ready, awaiting release', unit: 'count', basis: 'snapshot' },
  { group: 'pickup', key: 'released', label: 'Released to customer', unit: 'count', basis: 'window' },
  // delivery
  { group: 'delivery', key: 'inProgress', label: 'In delivery now', unit: 'count', basis: 'snapshot' },
  { group: 'delivery', key: 'delivered', label: 'Delivered', unit: 'count', basis: 'window' },
  { group: 'delivery', key: 'handoffs', label: 'Delivery actions', unit: 'count', basis: 'window' },
  // value (integer centavos)
  { group: 'value', key: 'totalCents', label: 'Order value', unit: 'pesos', basis: 'window' },
  { group: 'value', key: 'paidCents', label: 'Paid', unit: 'pesos', basis: 'window' },
  { group: 'value', key: 'outstandingCents', label: 'Outstanding', unit: 'pesos', basis: 'window' },
  // discrepancies
  { group: 'discrepancies', key: 'events', label: 'Discrepancy events', unit: 'count', basis: 'window' },
  { group: 'discrepancies', key: 'netDifference', label: 'Net item difference', unit: 'delta', basis: 'window' },
  // exceptions (detection snapshot)
  { group: 'exceptions', key: 'total', label: 'Observations', unit: 'count', basis: 'snapshot' },
  { group: 'exceptions', key: 'error', label: 'Errors', unit: 'count', basis: 'snapshot' },
  { group: 'exceptions', key: 'attention', label: 'Attention', unit: 'count', basis: 'snapshot' },
  { group: 'exceptions', key: 'info', label: 'Info', unit: 'count', basis: 'snapshot' },
  // inventory
  { group: 'inventory', key: 'items', label: 'Active items', unit: 'count', basis: 'snapshot' },
  { group: 'inventory', key: 'lowStock', label: 'Low stock', unit: 'count', basis: 'snapshot' },
  { group: 'inventory', key: 'movements', label: 'Stock movements', unit: 'count', basis: 'window' },
  // purchases
  { group: 'purchases', key: 'count', label: 'Documents', unit: 'count', basis: 'window' },
  { group: 'purchases', key: 'totalCents', label: 'Document value', unit: 'pesos', basis: 'window' },
  { group: 'purchases', key: 'postedCount', label: 'Posted', unit: 'count', basis: 'window' },
  { group: 'purchases', key: 'postedCents', label: 'Posted spend', unit: 'pesos', basis: 'window' },
  // expenses
  { group: 'expenses', key: 'count', label: 'Records', unit: 'count', basis: 'window' },
  { group: 'expenses', key: 'amountCents', label: 'Total spent', unit: 'pesos', basis: 'window' },
];

/* ---------------- per-group metric shapes ---------------- */

export interface ComparisonTransactionsMetrics {
  /** Transactions received (created) in the window. */
  received: number;
  /** Transactions whose DONE transition was recorded in the window. */
  done: number;
  /** Current backlog: transactions not yet DONE (ignores the date filter). */
  activeNow: number;
}

export interface ComparisonWorkloadMetrics {
  /** Processing steps with endedAt set, created in the window. */
  processingCompleted: number;
  /** FOLDING subset of `processingCompleted`. */
  foldingCompleted: number;
  /** Steps started but not finished right now (ignores the date filter). */
  openSteps: number;
}

export interface ComparisonPickupMetrics {
  /** READY + pick-up transactions waiting on the counter right now. */
  readyAwaiting: number;
  /** Immutable release records written in the window (method = PICKUP). */
  released: number;
}

export interface ComparisonDeliveryMetrics {
  /** Delivery orders not yet closed (deliveryStatus != DELIVERY_DONE) right now. */
  inProgress: number;
  /** Handoff rows that moved a delivery to DELIVERED in the window. */
  delivered: number;
  /** Every delivery action (assign / hand off / delivered / closed) in the window. */
  handoffs: number;
}

export interface ComparisonValueMetrics {
  /** Sum of transaction totals received in the window (integer centavos). */
  totalCents: number;
  /** Sum of amounts recorded as paid on those transactions. */
  paidCents: number;
  /** totalCents - paidCents (raw; negative means overpaid). */
  outstandingCents: number;
}

export interface ComparisonDiscrepancyMetrics {
  /** Completed processing steps whose difference != 0 in the window. */
  events: number;
  /** Sum of those differences (negative = items short). */
  netDifference: number;
}

export interface ComparisonExceptionMetrics {
  /** All detections for the branch in the current scan. */
  total: number;
  error: number;
  attention: number;
  info: number;
}

export interface ComparisonInventoryMetrics {
  /** Active stock items right now. */
  items: number;
  /** Active items where quantity <= minQuantity right now (same rule as the inventory list). */
  lowStock: number;
  /** Inventory movements created in the window. */
  movements: number;
}

export interface ComparisonPurchaseMetrics {
  /** Purchase documents created in the window (any status). */
  count: number;
  /** Sum of their server-computed totals. */
  totalCents: number;
  /** POSTED documents (the ones that actually stocked in). */
  postedCount: number;
  /** Sum of posted totals = committed spend. */
  postedCents: number;
}

export interface ComparisonExpenseMetrics {
  /** Expense records created in the window. */
  count: number;
  /** Sum of their amounts. */
  amountCents: number;
}

/** All ten groups, keyed exactly like `COMPARISON_GROUPS`. */
export interface ComparisonMetrics {
  transactions: ComparisonTransactionsMetrics;
  workload: ComparisonWorkloadMetrics;
  pickup: ComparisonPickupMetrics;
  delivery: ComparisonDeliveryMetrics;
  value: ComparisonValueMetrics;
  discrepancies: ComparisonDiscrepancyMetrics;
  exceptions: ComparisonExceptionMetrics;
  inventory: ComparisonInventoryMetrics;
  purchases: ComparisonPurchaseMetrics;
  expenses: ComparisonExpenseMetrics;
}

export interface ComparisonBranchRef {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

/** One branch's row: identity + every metric group. */
export interface BranchComparisonRow extends ComparisonMetrics {
  branch: ComparisonBranchRef;
}

export interface ComparisonRange {
  from: string | null;
  to: string | null;
}

export interface BranchComparisonResponse {
  /** Asia/Manila - all window boundaries derive from server clocks. */
  timeZone: string;
  /** Echo of the requested Manila dates; null = all recorded history. */
  range: ComparisonRange | null;
  /** Server-side time the snapshot metrics (and exception scan) were taken. */
  generatedAt: string;
  /** True when the bounded exception scan hit its cap (oldest first). */
  truncated: boolean;
  /** Rows ordered by branch code (deterministic). */
  branches: BranchComparisonRow[];
  /** Element-wise sum of `branches` (empty state = all zeros). */
  totals: ComparisonMetrics;
}

/* ---------------- helpers ---------------- */

/** All-zero metrics - the empty state and the base every row is filled into. */
export function emptyComparisonMetrics(): ComparisonMetrics {
  return {
    transactions: { received: 0, done: 0, activeNow: 0 },
    workload: { processingCompleted: 0, foldingCompleted: 0, openSteps: 0 },
    pickup: { readyAwaiting: 0, released: 0 },
    delivery: { inProgress: 0, delivered: 0, handoffs: 0 },
    value: { totalCents: 0, paidCents: 0, outstandingCents: 0 },
    discrepancies: { events: 0, netDifference: 0 },
    exceptions: { total: 0, error: 0, attention: 0, info: 0 },
    inventory: { items: 0, lowStock: 0, movements: 0 },
    purchases: { count: 0, totalCents: 0, postedCount: 0, postedCents: 0 },
    expenses: { count: 0, amountCents: 0 },
  };
}

/** Read one metric generically (used by the comparison table renderer). */
export function comparisonMetricValue(
  metrics: ComparisonMetrics,
  group: ComparisonGroupId,
  key: string,
): number {
  return (metrics[group] as unknown as Record<string, number>)[key] ?? 0;
}
